import { useAppDispatch, useAppSelector } from "@/app/appStore";
import { InfoItem } from "@/components/info-item";
import {
  addProximityTimeRange,
  setAnnotationData,
  selectAnnotationData,
  setSelectedEventById,
  setHighlightedTimelineEventId,
} from "@/entities/annotation";
import { Text, Flex, TextField, Button } from "@radix-ui/themes";
import { formatTimestamp, getHandSide } from "../../lib/helpers";
import { useState } from "react";
import { displayUUID, formatAbsoluteCoordinates } from "@/shared";
import { useTrackedHotkeys } from "@/shared/hooks/useTrackedHotkeys";
import { AnnotationFormWrapper } from "../../ui/annotation-form-wrapper";
import { useTimelineData } from "../../hooks/useTimelineData";
import {
  ANNOTATION_TYPES,
  ANNOTATION_RECORD_STATUSES,
} from "@/entities/annotation/model/consts";
import { usePlayer, usePlayerStore } from "@tezzro/player";

const ProximityTimestampPicker = ({
  label,
  value,
  onGetTime,
}: {
  label: string;
  value: string;
  onGetTime: (value: string) => void;
}) => {
  const { getState } = usePlayerStore();

  const handleGetTime = () => {
    onGetTime(new Date(getState().currentTime).toISOString());
  };

  return (
    <>
      <Text size="1" color="gray" className="min-w-fit">
        {label}:
      </Text>
      <Flex justify="between">
        <TextField.Root
          size="1"
          mx="1"
          className="flex-1"
          placeholder="Timestamp"
          value={value}
          disabled
        />
        <Button
          size="1"
          color="gray"
          variant="soft"
          type="button"
          onClick={handleGetTime}
        >
          Get time
        </Button>
      </Flex>
    </>
  );
};

const ProximityTimeRangesAnnotationForm = () => {
  const dispatch = useAppDispatch();
  const { addTimelineProximityTimeRangeEvents } = useTimelineData();
  const annotationData = useAppSelector(selectAnnotationData);
  const player = usePlayer();
  const [beforeRangeStartTime, setBeforeRangeStartTime] = useState("");
  const [beforeRangeEndTime, setBeforeRangeEndTime] = useState("");
  const [afterRangeStartTime, setAfterRangeStartTime] = useState("");
  const [afterRangeEndTime, setAfterRangeEndTime] = useState("");
  const [note, setNote] = useState("");

  const handleCancel = () => {
    dispatch(setAnnotationData(null));
  };

  const handleApply = () => {
    if (
      !annotationData?.proximityId ||
      !annotationData.handId ||
      !annotationData.gondolaId ||
      !annotationData.gondolaName
    ) {
      return null;
    }

    const customId = crypto.randomUUID();

    const proximityTimeRangeData = {
      type: ANNOTATION_TYPES.PROXIMITY_TIME_RANGES,
      status: ANNOTATION_RECORD_STATUSES.NEW,
      id: customId,
      orderId: annotationData.orderId,
      journeyId: annotationData.journeyId,
      proximityId: annotationData.proximityId,
      handId: annotationData.handId,
      timestamp: annotationData.timestamp,
      cameraImageWidth: annotationData.cameraImageWidth,
      cameraImageHeight: annotationData.cameraImageHeight,
      pinCoordinates: annotationData.pinCoordinates,
      cameraId: Number(annotationData.cameraId),
      calibrationId: annotationData.calibrationId,
      gondolaId: annotationData.gondolaId,
      gondolaName: annotationData.gondolaName,
      planogramId: annotationData.planogramId,
      beforeRangeStartTime,
      beforeRangeEndTime,
      afterRangeStartTime,
      afterRangeEndTime,
      notes: note,
    };

    dispatch(addProximityTimeRange(proximityTimeRangeData));
    dispatch(setSelectedEventById(customId));
    dispatch(setHighlightedTimelineEventId(customId));
    addTimelineProximityTimeRangeEvents(proximityTimeRangeData);
    player.seek(annotationData?.timestamp, annotationData.cameraId);
    dispatch(setAnnotationData(null));
  };

  useTrackedHotkeys("esc", (e) => {
    e.preventDefault();
    handleCancel();
  });

  useTrackedHotkeys("mod + enter", (e) => {
    e.preventDefault();
    handleApply();
  });

  const canSave =
    beforeRangeStartTime &&
    beforeRangeEndTime &&
    afterRangeStartTime &&
    afterRangeEndTime;

  if (!annotationData?.proximityId || !annotationData.handId) {
    return null;
  }

  const pinCoordinates = formatAbsoluteCoordinates(
    annotationData?.pinCoordinates.relativeX,
    annotationData?.pinCoordinates.relativeY,
    annotationData.cameraImageWidth,
    annotationData.cameraImageHeight
  );

  return (
    <AnnotationFormWrapper>
      <InfoItem label="Event type" value="Proximity Time Ranges Annotation" />
      <InfoItem
        label="Journey ID"
        copyValue={annotationData?.journeyId}
        value={displayUUID(annotationData?.journeyId)}
      />
      <InfoItem
        label="Order ID"
        value={displayUUID(annotationData.orderId)}
        copyValue={annotationData.orderId}
      />
      <InfoItem
        label="Proximity Annotation ID"
        value={
          annotationData.proximityId
            ? displayUUID(annotationData.proximityId)
            : undefined
        }
        copyValue={annotationData.proximityId || undefined}
      />

      <InfoItem
        label="Timestamp"
        value={
          annotationData?.timestamp &&
          formatTimestamp(annotationData?.timestamp)
        }
        copyValue={annotationData?.timestamp}
      />
      <InfoItem label="Hand" value={getHandSide(annotationData.handId)} />

      <ProximityTimestampPicker
        label="Before range start time"
        value={beforeRangeStartTime}
        onGetTime={setBeforeRangeStartTime}
      />
      <ProximityTimestampPicker
        label="Before range end time"
        value={beforeRangeEndTime}
        onGetTime={setBeforeRangeEndTime}
      />
      <ProximityTimestampPicker
        label="After range start time"
        value={afterRangeStartTime}
        onGetTime={setAfterRangeStartTime}
      />
      <ProximityTimestampPicker
        label="After range end time"
        value={afterRangeEndTime}
        onGetTime={setAfterRangeEndTime}
      />
      <InfoItem label="Pin coordinates (x,y)" value={pinCoordinates} />
      <InfoItem
        label="Camera image dimensions"
        value={`${annotationData?.cameraImageWidth}x${annotationData?.cameraImageHeight}`}
      />
      <InfoItem label="Camera ID" value={annotationData?.cameraId} />
      <InfoItem label="Calibration ID" value={annotationData?.calibrationId} />
      <InfoItem label="Gondola name" value={annotationData?.gondolaName} />
      <InfoItem label="Gondola ID" value={annotationData?.gondolaId} />
      <InfoItem label="Planogram ID" value={annotationData?.planogramId} />
      <>
        <Text size="1" color="gray" className="min-w-fit">
          Note:
        </Text>
        <TextField.Root
          size="1"
          mx="1"
          placeholder="Some kind of note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </>

      <Flex justify="between" mt="8">
        <Button
          size="1"
          color="gray"
          variant="soft"
          type="button"
          onClick={handleCancel}
        >
          Cancel (esc)
        </Button>
        <Button
          size="1"
          color="gray"
          highContrast
          type="submit"
          onClick={handleApply}
          disabled={!canSave}
        >
          Apply (ctrl + enter)
        </Button>
      </Flex>
    </AnnotationFormWrapper>
  );
};

export default ProximityTimeRangesAnnotationForm;
